import { useEffect, useState } from 'react';
import { Calendar, Check, FileText, Pencil, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { formatEastern } from '@/lib/format';
import { filesRemoved } from '@/lib/retentionNotice';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { StyleNotes } from '@/components/StyleNotes';
import { StyleProfileView } from '@/components/StyleProfileView';

/**
 * The one Notes section at the top of the project's Notes tab (Ryan, 2026-09-25), for the assigned editor, admin
 * and staff, and on the Redemption Cut page. In this order:
 *   1. This project: what the client wrote for this order, exactly as written (the order notes, their music, the
 *      notes they added later, how they sent the files, the language). The first thing an editor reads.
 *   2. The AI style notes for this brand and product type (components/StyleNotes.js), carrying the whole Style
 *      Profile; with none yet, or none that carry it all, the Style Profile itself in their place.
 *   3. "See what the client wrote in their Style Profile", closed until opened.
 * It replaces every separate block that showed the client's notes or Style Profile, for editors and for the
 * team. Admin and staff also get, here: the pencil on the order notes (`onSaveNotes`), "Add a note" and the line
 * controls in the AI notes, and the link to the full Style Profile (`onOpenProfile`, the Profile tab).
 * `renderProfile(profile)` renders the drawer's Style Profile view; `fallbackProfile` is the drawer's own copy,
 * for when the notes cannot say which profile they were checked against; `notesUrl` reads the AI notes another way.
 */
const LABEL = 'font-sans text-[9px] font-semibold uppercase tracking-wider';
const MUSIC_CHOICE = {
  uncut_gems_chooses: 'Uncut Gems selects music', client_provides: 'Client provides music', na_culling: 'N/A, culling only',
};

function Written({ label, testid, children }) {
  return (
    <div>
      <span className={`${LABEL} text-slate-500 block mb-0.5`}>{label}</span>
      <p className="text-[12px] text-zinc-100/85 whitespace-pre-wrap break-words leading-relaxed" data-testid={testid}>{children}</p>
    </div>
  );
}

function SongFile({ project, file }) {
  const url = file.file_id && !filesRemoved(project) ? `${process.env.REACT_APP_BACKEND_URL}/api/storage/serve/${file.file_id}` : '';
  const presigned = async () => {
    try {
      const r = await api.post('/storage/presign-download', { key: file.url });
      window.location.href = r.data.download_url;
    } catch (err) {
      toast.error(err.response?.status === 410
        ? (err.response.data?.message || 'This file is no longer available in storage. Please contact support.')
        : 'Download failed');
    }
  };
  const link = 'tap-auto text-[11px] text-oxidized-teal hover:text-oxidized-teal/80 underline underline-offset-2 truncate';
  return (
    <div className="flex items-center gap-2 p-1.5 rounded bg-ug-surface/50 border border-ug-border-subtle" data-testid="notes-music-file">
      <FileText className="w-3 h-3 text-oxidized-teal shrink-0" />
      {url ? <a href={url} className={`${link} flex-1`}>{file.filename}</a>
        : file.url && !filesRemoved(project) ? <button type="button" onClick={presigned} className={`${link} flex-1 text-left`}>{file.filename}</button>
          : <span className="text-[11px] text-zinc-100/80 flex-1 truncate">{file.filename}</span>}
    </div>
  );
}

function ThisProject({ project, files, onSaveNotes }) {
  const [editing, setEditing] = useState(null);   // the order notes being edited by the team
  const [saving, setSaving] = useState(false);
  useEffect(() => { setEditing(null); }, [project.id]);   // never carry one project's draft onto the next
  const video = project.project_type !== 'photo';
  const added = project.additional_notes || [];
  const sent = video ? project.video_transfer_notes : project.photo_transfer_notes;
  const songs = video ? (files || []).filter(f => f.file_type === 'music') : [];
  const written = [project.notes, video && project.music_notes, sent].some(t => (t || '').trim()) || added.length > 0;
  const save = async () => {
    setSaving(true);
    try { await onSaveNotes(editing); setEditing(null); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-2.5" data-testid="notes-this-project">
      <div className="flex items-center gap-2">
        <span className={`${LABEL} text-ug-gold/60`}>This project</span>
        {onSaveNotes && editing === null && (
          <button type="button" onClick={() => setEditing(project.notes || '')} data-testid="notes-edit-order-notes"
            title="Edit the order notes" className="tap-auto text-slate-500 hover:text-ug-gold transition-colors">
            <Pencil className="w-3 h-3" />
          </button>
        )}
      </div>
      {editing !== null ? (
        <div>
          <span className={`${LABEL} text-slate-500 block mb-1`}>Order notes</span>
          <textarea data-testid="admin-notes-textarea" value={editing} onChange={e => setEditing(e.target.value)} rows={6} autoFocus
            className="w-full bg-ug-surface border border-ug-border text-zinc-100 rounded-md p-3 text-sm focus:border-gold-leaf/40 outline-none resize-y placeholder:text-white/40"
            placeholder="Project notes..." />
          <div className="flex gap-2 mt-2 justify-end">
            <Button size="sm" variant="ghost" onClick={() => setEditing(null)} data-testid="notes-edit-order-notes-cancel"
              className="tap-auto text-slate-500 h-8 text-[10px] px-2">Cancel</Button>
            <Button size="sm" onClick={save} disabled={saving} data-testid="notes-edit-order-notes-save"
              className="tap-auto bg-gold-leaf text-obsidian hover:bg-gold-leaf/90 h-8 text-[10px] px-3">Save</Button>
          </div>
        </div>
      ) : (
        <>
          {!written && <p className="text-[12px] text-slate-500" data-testid="notes-this-project-empty">No notes for this project.</p>}
          {(project.notes || '').trim() && <Written label="Order notes" testid="notes-order-notes">{project.notes}</Written>}
        </>
      )}
      {video && project.music_choice && (
        <div className="space-y-1.5" data-testid="notes-music">
          <span className={`${LABEL} text-slate-500 block`}>Music</span>
          <div className="flex items-center gap-2">
            <span className="text-[12px] text-zinc-100/85">{MUSIC_CHOICE[project.music_choice] || project.music_choice}</span>
            <div className={`ready-indicator ${project.music_ready ? 'ready' : 'not-ready'}`} style={{ width: 16, height: 16 }}>
              {project.music_ready ? <Check className="w-2.5 h-2.5" strokeWidth={2.5} /> : <X className="w-2.5 h-2.5" strokeWidth={2} />}
            </div>
          </div>
          {(project.music_notes || '').trim() && (
            <p className="text-[12px] text-zinc-100/85 whitespace-pre-wrap break-words leading-relaxed" data-testid="notes-music-notes">{project.music_notes}</p>
          )}
          {songs.map(f => <SongFile key={f.id} project={project} file={f} />)}
        </div>
      )}
      {added.length > 0 && (
        <div className="space-y-1.5" data-testid="notes-added">
          <span className={`${LABEL} text-slate-500 block`}>Added later</span>
          {added.map((n, i) => (
            <div key={n.id || i} data-testid="notes-added-entry">
              <span className="text-[10px] text-slate-500">{n.author_name} &middot; {formatEastern(n.created_at)}</span>
              <p className="text-[12px] text-zinc-100/85 whitespace-pre-wrap break-words leading-relaxed">{n.body}</p>
            </div>
          ))}
        </div>
      )}
      {(sent || '').trim() && <Written label="How the files were sent" testid="notes-files-sent">{sent}</Written>}
      {project.wedding_language && (
        <p className="text-[12px]" data-testid="notes-language">
          <span className={`${LABEL} text-slate-500`}>Language: </span>
          <span className={project.wedding_language === 'English' ? 'text-zinc-100/85' : 'text-ug-gold'}>{project.wedding_language}</span>
        </p>
      )}
    </div>
  );
}

export function NotesSection({ project, files, fallbackProfile, renderProfile, notesUrl, onSaveNotes, onOpenProfile }) {
  return (
    <section className="!mt-2" data-testid="notes-section">
      <h3 className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70 mb-3">Notes</h3>
      <div className="bg-ug-surface/50 rounded-xl border border-ug-border p-3 space-y-3">
        <ThisProject project={project} files={files} onSaveNotes={onSaveNotes} />
        <div className="pt-3 border-t border-ug-border space-y-3">
          <StyleNotes project={project} fallbackProfile={fallbackProfile} renderProfile={renderProfile} url={notesUrl} />
        </div>
        {onOpenProfile && (
          <button type="button" onClick={onOpenProfile} data-testid="notes-open-style-profile"
            className="tap-auto block mt-3 text-[10px] text-oxidized-teal hover:text-oxidized-teal/80 underline underline-offset-2 text-left">
            Open the full Style Profile
          </button>
        )}
      </div>
    </section>
  );
}

/**
 * The Redemption Cut page's Notes tab (Ryan, 2026-09-25): the same Notes section, read through the redemption,
 * in place of its old Notes boxes and Style Profile tab. The event date sits above it as it does in the drawer.
 */
export function RedemptionNotes({ detail }) {
  const { user } = useAuth();
  const editor = user?.role === 'editor';
  const project = detail.project || {};
  return (
    <div className="max-w-[1280px] mx-auto grid grid-cols-1 xl:grid-cols-12 gap-6" data-testid="redemption-notes">
      <div className="xl:col-span-7 min-w-0 space-y-4">
        <div className="tap-auto flex items-center gap-2 pb-3 border-b border-ug-border-subtle leading-none" data-testid="notes-event-date">
          <Calendar className="w-3.5 h-3.5 text-ug-gold/60 tap-auto shrink-0" strokeWidth={1.5} />
          <span className="font-sans text-[10px] font-semibold uppercase tracking-wider text-slate-500 leading-none">Event Date:</span>
          <span className="tap-auto font-sans text-[13px] font-medium text-zinc-100/90 leading-none">
            {project.event_date
              ? new Date(project.event_date + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
              : 'Not set'}
          </span>
        </div>
        <NotesSection project={project} files={[]} fallbackProfile={detail.style_profile}
          renderProfile={(p) => <StyleProfileView profile={p} showOnly={project.project_type} project={project}
            isEditor={editor} excludeAssets={editor} showMustHaves />}
          notesUrl={`/redemptions/${detail.id}/style-notes`} />
      </div>
    </div>
  );
}
